import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Phone, Mail, Save, Stethoscope, X, Plus, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { SPECIALTIES } from "@/pages/Providers";

const SPEC_TONE = {
  Psychiatrist: "from-indigo-500 to-indigo-600",
  PMHNP: "from-emerald-500 to-emerald-600",
  PsychD: "from-violet-500 to-violet-600",
  LCSW: "from-orange-500 to-orange-600",
  APRN: "from-sky-500 to-sky-600",
};

/** Normalize providers_map[spec] to always be an array (backend already does this, but be safe with legacy data). */
function pmToArray(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val.filter(v => v && v.provider_id);
  if (typeof val === "object" && val.provider_id) return [val];
  return [];
}

export default function FacilityProviders({ facility }) {
  const [providers, setProviders] = useState([]);           // full active roster (for the Add dropdown)
  const [attached, setAttached] = useState({});             // { spec: [entry, ...] } — entries carry all metadata
  const [pending, setPending] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // We fetch the full roster (not just active) so we can still Re-add someone who was flipped inactive
    api.get("/providers").then(({ data }) => setProviders(data));
    const current = facility?.providers_map || {};
    const m = {};
    SPECIALTIES.forEach((s) => { m[s] = pmToArray(current[s]); });
    setAttached(m);
    const p = {}; SPECIALTIES.forEach(s => { p[s] = ""; });
    setPending(p);
  }, [facility]);

  const provById = useMemo(() => {
    const m = new Map();
    providers.forEach(p => m.set(p.provider_id, p));
    return m;
  }, [providers]);

  const addFor = (spec) => {
    const pid = pending[spec];
    if (!pid) return;
    const p = provById.get(pid);
    if (!p) return;
    setAttached(prev => {
      const arr = prev[spec] || [];
      if (arr.some(x => x.provider_id === pid)) return prev;
      return { ...prev, [spec]: [...arr, { provider_id: p.provider_id, name: p.name, phone: p.phone, email: p.email, active: p.active, available_days: p.available_days || [] }] };
    });
    setPending(prev => ({ ...prev, [spec]: "" }));
  };
  const removeFor = (spec, pid) => {
    setAttached(prev => ({ ...prev, [spec]: (prev[spec] || []).filter(x => x.provider_id !== pid) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      SPECIALTIES.forEach((s) => { payload[s] = (attached[s] || []).map(x => x.provider_id); });
      await api.put(`/facilities/${facility.facility_id}/providers`, payload);
      toast.success("Providers saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Card className="p-6 border-slate-200 shadow-none" data-testid="facility-providers">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest font-mono-plex text-slate-500 flex items-center gap-1.5"><Stethoscope className="w-3 h-3" /> Care team</div>
          <h3 className="font-heading font-bold text-xl">Visiting / treating providers</h3>
          <p className="text-xs text-slate-500 mt-0.5">You can attach more than one provider per specialty for coverage.</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="fac-prov-save" className="bg-slate-900 hover:bg-slate-800"><Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save"}</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {SPECIALTIES.map((spec) => {
          const attachedIds = new Set((attached[spec] || []).map(x => x.provider_id));
          const options = providers.filter((p) => p.specialty === spec && p.active !== false && !attachedIds.has(p.provider_id));
          const currentList = attached[spec] || [];
          return (
            <div key={spec} className="rounded-lg border border-slate-200 overflow-hidden" data-testid={`fac-prov-${spec}`}>
              <div className={`px-3 py-1.5 text-white text-xs uppercase tracking-widest font-mono-plex bg-gradient-to-r ${SPEC_TONE[spec]} flex items-center justify-between`}>
                <span>{spec}</span>
                <span className="text-white/80 font-mono-plex">{currentList.length}</span>
              </div>
              <div className="p-3 space-y-2">
                {currentList.length === 0 && <div className="text-xs text-slate-400 italic">No {spec} attached yet.</div>}
                {currentList.map((p) => {
                  // Prefer live roster data (fresh phone/days) but fall back to what facility.providers_map stored
                  const live = provById.get(p.provider_id) || {};
                  const merged = { ...p, ...live, available_days: live.available_days ?? p.available_days ?? [] };
                  const isInactive = merged.active === false;
                  return (
                    <div key={merged.provider_id} className="flex items-start justify-between gap-2 border border-slate-100 rounded-md p-2 bg-slate-50" data-testid={`fac-prov-chip-${spec}-${merged.provider_id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-1.5">
                          {merged.name}
                          {isInactive && <Badge className="bg-slate-400 hover:bg-slate-400 text-[9px]" data-testid={`fac-prov-inactive-${spec}-${merged.provider_id}`}>Inactive</Badge>}
                        </div>
                        <div className="mt-0.5 text-[11px] text-slate-500 space-y-0.5">
                          {merged.phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3 text-slate-400" /> {merged.phone}</div>}
                          {merged.email && <div className="flex items-center gap-1"><Mail className="w-3 h-3 text-slate-400" /> {merged.email}</div>}
                          {merged.available_days?.length > 0 && (
                            <div className="flex items-center gap-1 flex-wrap"><CalendarClock className="w-3 h-3 text-slate-400" />
                              {merged.available_days.map(d => <span key={d} className="px-1 rounded bg-white border border-slate-200 text-[9px] font-mono-plex text-slate-600">{d}</span>)}
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => removeFor(spec, merged.provider_id)}
                        data-testid={`fac-prov-remove-${spec}-${merged.provider_id}`}
                        className="text-slate-400 hover:text-rose-500 p-1 rounded"
                        title="Remove"
                      ><X className="w-3.5 h-3.5" /></button>
                    </div>
                  );
                })}

                <div className="flex gap-2 items-center pt-1">
                  <Select value={pending[spec] || ""} onValueChange={(v) => setPending(prev => ({ ...prev, [spec]: v }))}>
                    <SelectTrigger className="text-xs h-8 flex-1" data-testid={`fac-prov-select-${spec}`}>
                      <SelectValue placeholder={options.length ? `Add ${spec}…` : `No more available`} />
                    </SelectTrigger>
                    <SelectContent>
                      {options.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">All active {spec}s are already attached.</div>}
                      {options.map(p => (
                        <SelectItem key={p.provider_id} value={p.provider_id}>
                          {p.name}{p.available_days?.length ? ` · ${p.available_days.join(",")}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm" variant="outline"
                    disabled={!pending[spec]}
                    onClick={() => addFor(spec)}
                    data-testid={`fac-prov-add-${spec}`}
                    className="h-8"
                  ><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
                </div>
                <Label className="text-[10px] uppercase tracking-widest font-mono-plex text-slate-400">click Save to persist</Label>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
